// Code generated by erp-api-gateway/cmd/contract; DO NOT EDIT.
// Contract SHA-256: 89c920f8e404aa0740e580ffe322895e8f684634e5b36cc677e304a84bce2a0a

import { z } from "zod/v3";

export type QueryPrimitive = string | number | boolean;
export type QueryValue =
  | QueryPrimitive
  | readonly QueryPrimitive[]
  | null
  | undefined;
export type QueryParameters = Readonly<Record<string, QueryValue>>;

export interface ApiErrorDetails {
  readonly fields?: Readonly<Record<string, readonly string[]>>;
  readonly [key: string]: unknown;
}

export interface ApiErrorPayload {
  readonly code: string;
  readonly message: string;
  readonly details: ApiErrorDetails;
  readonly request_id: string;
  readonly correlation_id: string;
}

export interface ApiErrorEnvelope {
  readonly error: ApiErrorPayload;
}

export interface ApiResponseHeaders {
  readonly retryAfter: string | null;
  readonly allow: string | null;
  readonly wwwAuthenticate: string | null;
  readonly contentType: string | null;
}

export const apiErrorDetailsSchema = z
  .record(z.unknown())
  .and(
    z.object({
      fields: z.record(z.array(z.string())).optional(),
    }),
  );

export const apiErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    details: apiErrorDetailsSchema,
    request_id: z.string().uuid(),
    correlation_id: z.string().uuid(),
  }),
});

export class ApiClientError<
  TStatus extends number = number,
  TCode extends string = string,
> extends Error {
  readonly name = "ApiClientError";
  readonly kind = "api";

  constructor(
    readonly operationId: string,
    readonly status: TStatus,
    readonly code: TCode,
    message: string,
    readonly details: ApiErrorDetails,
    readonly requestId: string,
    readonly correlationId: string,
    readonly responseHeaders: ApiResponseHeaders,
  ) {
    super(message);
  }
}

export class ApiTransportError extends Error {
  readonly name = "ApiTransportError";
  readonly kind = "transport";

  constructor(
    readonly operationId: string,
    message: string,
    readonly cause: unknown,
  ) {
    super(message);
  }
}

export class ApiAbortError extends Error {
  readonly name = "ApiAbortError";
  readonly kind = "abort";

  constructor(
    readonly operationId: string,
    readonly cause: unknown,
  ) {
    super("The API request was aborted.");
  }
}

export class ApiResponseValidationError extends Error {
  readonly name = "ApiResponseValidationError";
  readonly kind = "invalid_success_payload";

  constructor(
    readonly operationId: string,
    readonly status: number,
    readonly issues: z.ZodIssue[],
  ) {
    super("The API returned an invalid success response.");
  }
}

export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

export function isValidationApiError(
  error: unknown,
): error is ApiClientError<422, "validation.failed"> & {
  readonly details: ApiErrorDetails & {
    readonly fields: Readonly<Record<string, readonly string[]>>;
  };
} {
  return (
    isApiClientError(error) &&
    error.status === 422 &&
    error.code === "validation.failed" &&
    error.details.fields !== undefined
  );
}

export function isApiTransportError(
  error: unknown,
): error is ApiTransportError {
  return error instanceof ApiTransportError;
}

export function isApiAbortError(error: unknown): error is ApiAbortError {
  return error instanceof ApiAbortError;
}

export function isApiResponseValidationError(
  error: unknown,
): error is ApiResponseValidationError {
  return error instanceof ApiResponseValidationError;
}

export interface ApiRequestMetadata {
  readonly signal?: AbortSignal;
  readonly headers?: HeadersInit;
  readonly requestId?: string;
  readonly correlationId?: string;
}

export interface ApiOperationRequest<TPath, TQuery, TBody>
  extends ApiRequestMetadata {
  readonly path?: TPath;
  readonly query?: TQuery;
  readonly body?: TBody;
}

export interface ApiClientRequestDefaults {
  readonly headers?: HeadersInit;
  readonly requestId?: string;
  readonly correlationId?: string;
}

export interface ApiClientConfig {
  readonly baseUrl: string | URL;
  readonly fetch?: typeof globalThis.fetch;
  readonly credentials?: RequestCredentials;
  readonly headers?: HeadersInit;
  readonly requestDefaults?: () => ApiClientRequestDefaults;
  readonly readCookie?: (name: string) => string | null;
  readonly createId?: () => string;
}

export interface ApiOperationDefinition<TPath, TQuery, TBody, TSuccess> {
  readonly operationId: string;
  readonly method: "HEAD" | "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly pathTemplate: string;
  readonly pathSchema: z.ZodType<TPath>;
  readonly querySchema: z.ZodType<TQuery>;
  readonly bodySchema: z.ZodType<TBody>;
  readonly successSchema: z.ZodType<TSuccess>;
  readonly responseMode: "json" | "text" | "binary" | "empty";
}

const unsafeMethods = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiClient {
  readonly #baseUrl: URL;
  readonly #fetch: typeof globalThis.fetch;
  readonly #credentials: RequestCredentials;
  readonly #headers: Headers;
  readonly #requestDefaults?: () => ApiClientRequestDefaults;
  readonly #readCookie: (name: string) => string | null;
  readonly #createId: () => string;

  constructor(config: ApiClientConfig) {
    this.#baseUrl = normalizeBaseUrl(config.baseUrl);
    const fetchImplementation = config.fetch ?? globalThis.fetch?.bind(globalThis);
    if (fetchImplementation === undefined) {
      throw new Error("ApiClient requires a fetch implementation.");
    }
    this.#fetch = fetchImplementation;
    this.#credentials = config.credentials ?? "include";
    this.#headers = new Headers(config.headers);
    this.#requestDefaults = config.requestDefaults;
    this.#readCookie = config.readCookie ?? readBrowserCookie;
    this.#createId = config.createId ?? createRequestId;
  }

  async execute<TPath, TQuery, TBody, TSuccess>(
    definition: ApiOperationDefinition<TPath, TQuery, TBody, TSuccess>,
    request: ApiOperationRequest<TPath, TQuery, TBody> = {},
  ): Promise<TSuccess> {
    const path = definition.pathSchema.parse(request.path);
    const query = definition.querySchema.parse(request.query);
    const body = definition.bodySchema.parse(request.body);
    const defaults = this.#requestDefaults?.() ?? {};
    const headers = new Headers(this.#headers);
    mergeHeaders(headers, defaults.headers);
    mergeHeaders(headers, request.headers);
    headers.set("Accept", responseAccept(definition.responseMode));
    headers.set("X-Requested-With", "XMLHttpRequest");
    headers.set(
      "X-Request-ID",
      request.requestId ?? defaults.requestId ?? this.#createId(),
    );
    headers.set(
      "X-Correlation-ID",
      request.correlationId ?? defaults.correlationId ?? this.#createId(),
    );

    let encodedBody: BodyInit | undefined;
    if (body !== undefined) {
      headers.set("Content-Type", "application/json");
      encodedBody = JSON.stringify(body);
    }
    if (unsafeMethods.has(definition.method)) {
      const csrfToken = this.#readCookie("XSRF-TOKEN");
      if (csrfToken !== null) {
        headers.set("X-XSRF-TOKEN", safeDecodeURIComponent(csrfToken));
      }
    }

    const url = buildUrl(
      this.#baseUrl,
      definition.pathTemplate,
      path,
      query,
    );
    let response: Response;
    try {
      response = await this.#fetch(url, {
        method: definition.method,
        headers,
        body: encodedBody,
        credentials: this.#credentials,
        signal: request.signal,
      });
    } catch (error) {
      if (isAbortFailure(error, request.signal)) {
        throw new ApiAbortError(definition.operationId, error);
      }
      throw new ApiTransportError(
        definition.operationId,
        "The API request failed before a response was received.",
        error,
      );
    }

    if (!response.ok) {
      throw await parseApiError(definition.operationId, response);
    }
    if (response.status === 204 || definition.responseMode === "empty") {
      return parseSuccess(
        definition.operationId,
        response.status,
        definition.successSchema,
        undefined,
      );
    }

    let payload: unknown;
    try {
      if (definition.responseMode === "text") {
        payload = await response.text();
      } else if (definition.responseMode === "binary") {
        payload = await response.arrayBuffer();
      } else {
        payload = await response.json();
      }
    } catch {
      throw new ApiResponseValidationError(
        definition.operationId,
        response.status,
        [
          {
            code: z.ZodIssueCode.custom,
            path: [],
            message: "Response body could not be decoded.",
          },
        ],
      );
    }

    return parseSuccess(
      definition.operationId,
      response.status,
      definition.successSchema,
      payload,
    );
  }

  async bootstrapCsrf(metadata: ApiRequestMetadata = {}): Promise<void> {
    const definition: ApiOperationDefinition<
      undefined,
      undefined,
      undefined,
      undefined
    > = {
      operationId: "getSanctumCsrfCookie",
      method: "GET",
      pathTemplate: "/sanctum/csrf-cookie",
      pathSchema: z.undefined(),
      querySchema: z.undefined(),
      bodySchema: z.undefined(),
      successSchema: z.undefined(),
      responseMode: "empty",
    };
    await this.execute(definition, metadata);
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}

async function parseApiError(
  operationId: string,
  response: Response,
): Promise<ApiClientError> {
  const responseHeaders = pickResponseHeaders(response.headers);
  const fallbackRequestId = response.headers.get("X-Request-ID") ?? "";
  const fallbackCorrelationId =
    response.headers.get("X-Correlation-ID") ?? "";
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // The stable invalid-error response below intentionally hides upstream data.
  }
  const parsed = apiErrorEnvelopeSchema.safeParse(payload);
  if (!parsed.success) {
    return new ApiClientError(
      operationId,
      response.status,
      "gateway.invalid_error_response",
      "The API returned an invalid error response.",
      {},
      fallbackRequestId,
      fallbackCorrelationId,
      responseHeaders,
    );
  }

  return new ApiClientError(
    operationId,
    response.status,
    parsed.data.error.code,
    parsed.data.error.message,
    parsed.data.error.details,
    parsed.data.error.request_id,
    parsed.data.error.correlation_id,
    responseHeaders,
  );
}

function parseSuccess<T>(
  operationId: string,
  status: number,
  schema: z.ZodType<T>,
  payload: unknown,
): T {
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiResponseValidationError(operationId, status, parsed.error.issues);
  }
  return parsed.data;
}

function buildUrl(
  baseUrl: URL,
  pathTemplate: string,
  path: unknown,
  query: unknown,
): URL {
  const pathValues = isRecord(path) ? path : {};
  const pathname = pathTemplate.replace(/\{([^}]+)\}/g, (_, name: string) => {
    const value = pathValues[name];
    if (typeof value !== "string" && typeof value !== "number") {
      throw new TypeError(`Missing path parameter: ${name}`);
    }
    return encodeURIComponent(String(value));
  });
  const url = new URL(pathname.replace(/^\//, ""), baseUrl);
  if (isRecord(query)) {
    for (const [name, value] of Object.entries(query)) {
      appendQueryValue(url.searchParams, name, value);
    }
  }
  return url;
}

function appendQueryValue(
  search: URLSearchParams,
  name: string,
  value: unknown,
): void {
  if (value === undefined || value === null) {
    return;
  }
  if (Array.isArray(value)) {
    // Laravel reads repeated query values only in the `name[]` form; a plain
    // repeated `name` collapses to the last value on the PHP side.
    const itemName = name.endsWith("[]") ? name : `${name}[]`;
    for (const item of value) {
      appendQueryValue(search, itemName, item);
    }
    return;
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    search.append(name, String(value));
    return;
  }
  throw new TypeError(`Unsupported query value for ${name}.`);
}

function normalizeBaseUrl(value: string | URL): URL {
  const url = new URL(value);
  if (!url.pathname.endsWith("/")) {
    url.pathname += "/";
  }
  return url;
}

function mergeHeaders(target: Headers, values?: HeadersInit): void {
  if (values === undefined) {
    return;
  }
  new Headers(values).forEach((value, name) => target.set(name, value));
}

function responseAccept(mode: ApiOperationDefinition<unknown, unknown, unknown, unknown>["responseMode"]): string {
  if (mode === "text") {
    return "text/plain, application/yaml, application/json";
  }
  if (mode === "binary") {
    return "application/octet-stream, application/json";
  }
  return "application/json";
}

function pickResponseHeaders(headers: Headers): ApiResponseHeaders {
  return {
    retryAfter: headers.get("Retry-After"),
    allow: headers.get("Allow"),
    wwwAuthenticate: headers.get("WWW-Authenticate"),
    contentType: headers.get("Content-Type"),
  };
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function readBrowserCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const prefix = `${encodeURIComponent(name)}=`;
  for (const part of document.cookie.split(";")) {
    const cookie = part.trim();
    if (cookie.startsWith(prefix)) {
      return cookie.slice(prefix.length);
    }
  }
  return null;
}

function createRequestId(): string {
  if (globalThis.crypto?.randomUUID !== undefined) {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues !== undefined) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function isAbortFailure(error: unknown, signal?: AbortSignal): boolean {
  return (
    signal?.aborted === true ||
    (typeof DOMException !== "undefined" &&
      error instanceof DOMException &&
      error.name === "AbortError") ||
    (isRecord(error) && error.name === "AbortError")
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
